import { useNavigate } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import Logo from '../../components/common/Logo';

const PLANS = [
  {
    key: 'ORDINARY',
    label: 'Ordinary',
    price: 20000,
    lottie: 'https://lottie.host/ce56482f-7626-4f68-9764-d9be51dc068f/MuoCw0VFUV.lottie',
    color: '#6b7280',
    tint: '#f9fafb',
    border: '#e5e7eb',
    activeBorder: '#6b7280',
    badge: null,
    features: [
      'Profile listing in search results',
      'Up to 3 services listed',
      'Basic booking management',
      'Email notifications',
      'Booking count stats',
    ],
    missing: [
      'Portfolio / photo gallery',
      'Priority search placement',
      'Revenue analytics',
      'Home service option',
      'Verified badge',
    ],
  },
  {
    key: 'ADVANCED',
    label: 'Advanced',
    price: 30000,
    lottie: 'https://lottie.host/f4b4129f-f6bb-471f-b0e4-651240f1cca3/HpEvBtaIPZ.lottie',
    color: 'var(--orange-9)',
    tint: 'var(--orange-1)',
    border: 'var(--orange-4)',
    activeBorder: 'var(--orange-9)',
    badge: 'Popular',
    features: [
      'Everything in Ordinary',
      'Up to 10 services listed',
      'Portfolio & photo gallery',
      'Availability calendar',
      'Better search placement',
      'Revenue & booking analytics',
      'Push + email notifications',
      'Client reviews displayed',
    ],
    missing: [
      'Priority top placement',
      'Home service option',
      'Verified badge',
      'Custom booking link',
    ],
  },
  {
    key: 'PREMIUM',
    label: 'Premium',
    price: 80000,
    lottie: 'https://lottie.host/2abc7425-cb6a-47d1-9ef1-3b3146da39ed/KtZQULhYrQ.lottie',
    color: '#7c3aed',
    tint: '#faf5ff',
    border: '#ddd6fe',
    activeBorder: '#7c3aed',
    badge: 'Best Value',
    features: [
      'Everything in Advanced',
      'Unlimited services',
      'Priority top placement always',
      'Home service option',
      'Full analytics dashboard',
      'Verified badge on profile',
      'Custom shareable booking link',
      'Priority support',
    ],
    missing: [],
  },
];

const ProviderPlans = () => {
  const navigate = useNavigate();

  const handleSelect = (plan) => {
    navigate('/register/provider', {
      state: { plan: plan.key, planLabel: plan.label, planPrice: plan.price },
    });
  };

  return (
    <div
      style={{
        minHeight: '100dvh',
        backgroundColor: '#fff',
        fontFamily: "'Poppins', sans-serif",
        overflowX: 'hidden',
      }}
    >
      {/* Top bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 24px',
          borderBottom: '1px solid var(--gray-4)',
        }}
      >
        <Logo size="small" dark />
        <button
          type="button"
          onClick={() => navigate('/register')}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            fontSize: '13px', fontWeight: 500, color: 'var(--gray-10)',
            background: 'none', border: 'none', cursor: 'pointer',
            fontFamily: "'Poppins', sans-serif",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--orange-9)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--gray-10)')}
        >
          ← Back
        </button>
      </div>

      {/* Hero */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '40px 24px 24px',
          textAlign: 'center',
        }}
      >
        <div style={{ width: '140px', height: '140px', marginBottom: '20px' }}>
          <DotLottieReact
            src="https://lottie.host/b64dfeab-71c1-4bc2-afa3-de0d7a3d3797/KDSS4suTbY.lottie"
            loop
            autoplay
            style={{ width: '100%', height: '100%' }}
          />
        </div>

        <h1
          style={{
            fontSize: 'clamp(1.4rem, 4vw, 2rem)',
            fontWeight: 700,
            color: 'var(--gray-12)',
            margin: '0 0 10px',
            letterSpacing: '-0.02em',
          }}
        >
          Choose your plan
        </h1>
        <p
          style={{
            fontSize: '14px',
            color: 'var(--gray-10)',
            maxWidth: '420px',
            lineHeight: 1.6,
            margin: 0,
          }}
        >
          One payment unlocks your provider account. Pick the plan that fits where you are today — you can upgrade any time.
        </p>
      </div>

      {/* Plan cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '16px',
          maxWidth: '900px',
          margin: '0 auto',
          padding: '16px 20px 60px',
        }}
      >
        {PLANS.map((plan) => (
          <div
            key={plan.key}
            style={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              borderRadius: '20px',
              border: `1.5px solid ${plan.border}`,
              backgroundColor: plan.tint,
              padding: '28px 24px 24px',
              transition: 'box-shadow 0.18s, transform 0.18s, border-color 0.18s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 16px 40px -10px rgba(0,0,0,0.14)';
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.borderColor = plan.activeBorder;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.borderColor = plan.border;
            }}
          >
            {/* Popular / Best Value badge */}
            {plan.badge && (
              <span
                style={{
                  position: 'absolute',
                  top: '14px',
                  right: '14px',
                  fontSize: '10px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  padding: '3px 10px',
                  borderRadius: '999px',
                  backgroundColor: plan.key === 'PREMIUM' ? '#ede9fe' : 'var(--orange-2)',
                  color: plan.color,
                }}
              >
                {plan.badge}
              </span>
            )}

            {/* Lottie */}
            <div style={{ width: '100px', height: '100px', marginBottom: '12px' }}>
              <DotLottieReact
                src={plan.lottie}
                loop
                autoplay
                style={{ width: '100%', height: '100%' }}
              />
            </div>

            {/* Name + price */}
            <h2
              style={{
                fontSize: '18px',
                fontWeight: 700,
                color: 'var(--gray-12)',
                margin: '0 0 4px',
                letterSpacing: '-0.01em',
              }}
            >
              {plan.label}
            </h2>
            <p
              style={{
                fontSize: '26px',
                fontWeight: 800,
                color: plan.color,
                margin: '0 0 20px',
                lineHeight: 1,
              }}
            >
              UGX {plan.price.toLocaleString()}
              <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--gray-10)', marginLeft: '4px' }}>
                / month
              </span>
            </p>

            {/* Features */}
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: '0 0 auto',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                flexGrow: 1,
              }}
            >
              {plan.features.map((f) => (
                <li
                  key={f}
                  style={{
                    fontSize: '12px',
                    color: 'var(--gray-11)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  <span
                    style={{
                      marginTop: '2px',
                      flexShrink: 0,
                      width: '15px',
                      height: '15px',
                      borderRadius: '50%',
                      backgroundColor: plan.key === 'PREMIUM' ? '#ede9fe' : plan.key === 'ADVANCED' ? 'var(--orange-2)' : '#f3f4f6',
                      color: plan.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '9px',
                      fontWeight: 700,
                    }}
                  >
                    ✓
                  </span>
                  {f}
                </li>
              ))}
              {plan.missing.map((f) => (
                <li
                  key={f}
                  style={{
                    fontSize: '12px',
                    color: 'var(--gray-8)',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                    textDecoration: 'line-through',
                  }}
                >
                  <span
                    style={{
                      marginTop: '2px',
                      flexShrink: 0,
                      width: '15px',
                      height: '15px',
                      borderRadius: '50%',
                      backgroundColor: '#f3f4f6',
                      color: 'var(--gray-7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '9px',
                      fontWeight: 700,
                    }}
                  >
                    ×
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            {/* CTA */}
            <button
              type="button"
              onClick={() => handleSelect(plan)}
              style={{
                marginTop: '24px',
                width: '100%',
                padding: '12px 0',
                borderRadius: '10px',
                border: 'none',
                background: plan.key === 'PREMIUM'
                  ? 'linear-gradient(135deg, #7c3aed, #6d28d9)'
                  : plan.key === 'ADVANCED'
                    ? 'linear-gradient(135deg, var(--orange-9), #f97316)'
                    : '#1f2937',
                color: '#fff',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
                fontFamily: "'Poppins', sans-serif",
                boxShadow: plan.key === 'PREMIUM'
                  ? '0 6px 18px rgba(124,58,237,0.35)'
                  : plan.key === 'ADVANCED'
                    ? '0 6px 18px rgba(240,90,53,0.35)'
                    : '0 4px 12px rgba(0,0,0,0.18)',
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = '0.88')}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = '1')}
            >
              Get started with {plan.label}
            </button>
          </div>
        ))}
      </div>

      {/* Footer note */}
      <p
        style={{
          textAlign: 'center',
          fontSize: '12px',
          color: 'var(--gray-9)',
          paddingBottom: '32px',
          margin: 0,
        }}
      >
        Already have an account?{' '}
        <a
          href="/login"
          style={{ color: 'var(--orange-9)', fontWeight: 600, textDecoration: 'none' }}
        >
          Sign in
        </a>
      </p>
    </div>
  );
};

export default ProviderPlans;
